import { useCallback, useState } from "react";

import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";

import { ClientsService } from "../../services/clientsService";
import type { ClientAccount, CreateClientAccountRequest } from "../../types/clients-api.types";
import { ClientAccountCreateForm } from "./ClientAccountCreateForm";

export interface ClientAccountCreateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Client the new account is assigned to (POST /internal/clients/:clientId/accounts). */
  clientId: string;
  clientDisplayName: string;
  /** Preselects (and optionally locks) the currency — e.g. the dispersion destination currency. */
  defaultCurrency?: string;
  lockCurrency?: boolean;
  /** Called with the created account (after the form's success toast). The host closes the drawer. */
  onCreated: (account: ClientAccount) => void;
}

/**
 * Side-drawer wrapper around `ClientAccountCreateForm` — the same account form as /app/clients, presented
 * like the create-client drawer. The single-child body makes `ColocaDrawer` sticky-pin the form's submit row
 * to the bottom edge, so "Save account" stays visible while the fields scroll.
 */
export function ClientAccountCreateDrawer({
  isOpen,
  onClose,
  clientId,
  clientDisplayName,
  defaultCurrency,
  lockCurrency = false,
  onCreated,
}: ClientAccountCreateDrawerProps) {
  const { t } = useTranslations("clients");
  const [submitting, setSubmitting] = useState(false);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !submitting) onClose();
    },
    [onClose, submitting],
  );

  const createAccount = useCallback(
    async (body: CreateClientAccountRequest): Promise<ClientAccount> => {
      const { data } = await ClientsService.createAccount(clientId, body);
      return data;
    },
    [clientId],
  );

  return (
    <ColocaDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      size="lg"
      title={t("accounts_section_new")}
      description={t("accounts_drawer_assign_to", { name: clientDisplayName })}
    >
      {/* Mounted only while open so every opening starts from a clean form. */}
      {isOpen && (
        <ClientAccountCreateForm
          createAccount={createAccount}
          onCreated={onCreated}
          defaultCurrency={defaultCurrency}
          lockCurrency={lockCurrency}
          onSubmittingChange={setSubmitting}
        />
      )}
    </ColocaDrawer>
  );
}
